// ** React Imports
import { useEffect, useState, Fragment } from "react";

// ** MUI Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import FormHelperText from "@mui/material/FormHelperText";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";
import { useTheme } from "@mui/material/styles";
import MenuItem from "@mui/material/MenuItem";
import Avatar from "@mui/material/Avatar";
import PersonIcon from "@mui/icons-material/Person";

// ** Third Party Imports
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

// ** Custom Component Import
import CustomTextField from "../../../../components/mui/text-field";

// ** Icon Imports
import Icon from "../../../../components/icon";
import { useNavigate } from "react-router-dom";
import { dev_baseUrl } from "utils/var";

const StepAccountDetails = ({
  handleNext,
  personalDetails,
  setPersonalDetails,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const { firstName, lastName, phoneNumber, referredBy } = personalDetails;
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [referralText, setReferralText] = useState("");
  const [callText, setCallText] = useState("");
  const [emailText, setEmailText] = useState("");
  const [otherText, setOtherText] = useState("");

  const referralOptions = [
    "Referral",
    "Google",
    "Bing",
    "Facebook",
    "Instagram",
    "X (Twitter)",
    "LinkedIn",
    "YouTube",
    "Call",
    "Email",
    "Other",
  ];

  useEffect(() => {
    if (message || Object.keys(errors).length > 0) {
      const timer = setTimeout(() => {
        setMessage("");
        setErrors({});
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [message, errors]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (
        firstName.length > 0 ||
        lastName.length > 0 ||
        phoneNumber.length > 0
      ) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    const handlePopState = () => {
      if (
        firstName.length > 0 ||
        lastName.length > 0 ||
        phoneNumber.length > 0
      ) {
        const shouldNavigate = window.confirm(
          "Are you sure you want to go back? Your changes might not be saved."
        );
        if (!shouldNavigate) {
          window.history.pushState(null, "", window.location.href);
        }
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [firstName, lastName, phoneNumber]);

  const validateName = (name, field) => {
    const errors = {};
    const forbiddenKeywords = ["Select", "Update", "Delete", "INSERT", "Drop"];
    const nameRegex = /^[A-Za-z\s'-]+$/;

    if (!nameRegex.test(name)) {
      errors[field] = `${
        field === "firstName" ? "First" : "Last"
      } Name cannot contain special characters.`;
    }

    if (
      forbiddenKeywords.some((word) =>
        name.toLowerCase().includes(word.toLowerCase())
      )
    ) {
      errors[field] = `Invalid Input.`;
    }

    if ((name && name.trim().length) < 2 || (name && name.trim().length) > 50) {
      errors[field] = `${
        field === "firstName" ? "First" : "Last"
      } Name must be between 2 and 50 characters.`;
    }

    if (!name.trim()) {
      errors[field] = `${
        field === "firstName" ? "First" : "Last"
      } Name cannot be empty or only spaces.`;
    }

    if (!name) {
      errors[field] = `${
        field === "firstName" ? "First" : "Last"
      } Name is required.`;
    }

    return errors;
  };

  const onNextClick = async () => {
    const newErrors = {};
    const token = localStorage.getItem("jwtToken");

    if (!token) {
      setLoading(true);
      navigate("/login", {
        state: { message: "Session expired, please login again." },
      });
      return;
    }

    const firstNameErrors = validateName(firstName, "firstName");
    if (Object.keys(firstNameErrors).length > 0) {
      newErrors.firstName = firstNameErrors.firstName;
    }

    const lastNameErrors = validateName(lastName, "lastName");
    if (Object.keys(lastNameErrors).length > 0) {
      newErrors.lastName = lastNameErrors.lastName;
    }

    if (!phoneNumber || phoneNumber.trim() === "") {
      newErrors.phone = "Phone number is required.";
    } else if (phoneNumber.length < 10) {
      newErrors.phone = "Invalid phone number.";
    } else if (phoneNumber === "1") {
      newErrors.phone = "Invalid phone number.";
    } else if (phoneNumber.length !== 10) {
      newErrors.phone = "Phone number should be 10 digits";
    }

    if (!referredBy || referredBy.trim() === "") {
      newErrors.referralSource = "Please enter how you heard about us";
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    const apiPayload = {      
      "firstName": firstName,
      "lastName": lastName,    
      "referredBy": referredBy,   
      "jobTitle": "Owner",    
      "phoneNumber":`${phoneNumber}`
    };

    setLoading(true);

    try {
      const response = await fetch(
        `${dev_baseUrl}/Employee/UpdatePersonalDetail`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(apiPayload),
        }
      );

      if (response.status === 401) {
        navigate("/login", {
          state: { message: "Session expired, please login again." },
        });
        return;
      }

      const result = await response.json();

      if (result && result.isError) {
        setMessage(result?.message || "Failed to update personal details");
        return;
      }

      if (response.ok) {
        setMessage("Personal details updated successfully.");
        handleNext();
      } else {
        setMessage("Failed to update personal details.");
      }
    } catch (error) {
      setMessage("An error occurred while updating personal details.");
    } finally {
      setLoading(false);
    }
  };

return (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "100%",
      px: { xs: 2, sm: 0, md: 0 },
      pt: { xs: 5, sm: "70px", md: "100px", lg: 8, xl: 10 },
    }}
  >
    <Box width="100%" maxWidth="500px" sx={{ mx: "auto" }}>
      {" "}
      {message && (
        <Typography
          mb={2}
          textAlign="center"
          variant="h6"
          color={message.includes("successfully") ? "green" : "red"}
          sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
        >
          {message}
        </Typography>
      )}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: { xs: 3, md: 4 },
              ml: { xs: 0, md: 0 },
              flexDirection: { xs: "column", sm: "row" },
              textAlign: { xs: "center", sm: "left" },
            }}
          >
            <Avatar
              sx={{
                bgcolor: "transparent",
                border: "1px solid #e0e0e0",
                color: "text.primary",
                mr: { xs: 0, sm: 3 },
                mb: { xs: 2, sm: 0 },
                height: { xs: 40, sm: 50 },
                width: { xs: 38, sm: 48 },
              }}
            >
              <PersonIcon />
            </Avatar>
            <Box>
              <Typography
                variant="h5"
                sx={{
                  mb: 0.2,
                  fontSize: { xs: "1.23rem", sm: "1.23rem" },
                }}
              >
                Personal Details
              </Typography>
              <Typography
                sx={{
                  color: "text.secondary",
                  fontSize: { xs: "0.875rem", sm: "0.9rem" },
                }}
              >
                Let's get started! Provide a few details to set up your account
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* Personal Details Form */}
        <Grid item xs={12}>
            <FormControl
              fullWidth
              sx={{ display: "flex", justifyContent: "center" }}
            >
              <CustomTextField
                fullWidth
                label="First Name"
                placeholder="John"
                value={firstName}
                error={!!errors.firstName}
                helperText={errors.firstName}
                onChange={(e) => {
                  const newValue = e.target.value.replace(/[^a-zA-Z\s\-']/g, "");
                  setPersonalDetails({
                    ...personalDetails,
                    firstName: newValue,
                  });
                }}
                required
                sx={{ maxWidth: "500px" }}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl
              fullWidth
              sx={{ display: "flex", justifyContent: "center" }}
            >
              <CustomTextField
                fullWidth
                label="Last Name"
                placeholder="Smith"
                value={lastName}
                error={!!errors.lastName}
                helperText={errors.lastName}
                onChange={(e) => {
                  const newValue = e.target.value.replace(/[^a-zA-Z\s\-']/g, "");
                  setPersonalDetails({
                    ...personalDetails,
                    lastName: newValue,
                  });
                }}
                required
                sx={{ maxWidth: "500px" }}
              />
            </FormControl>
          </Grid>

        <Grid item xs={12}>
          <FormControl
            fullWidth
            sx={{ display: "flex", justifyContent: "center" }}
          >
            <Box sx={{ maxWidth: "500px", width: "100%" }}>
              {" "}
              {/* Fixed 500px width */}
              <FormLabel
                required
                sx={{
                  mb: 0.8,
                  fontSize: "0.8rem",
                  fontWeight: 400,
                  color: theme.palette.text.primary,
                  "& .MuiFormLabel-asterisk": {
                    color: theme.palette.error.main,
                  },
                }}
              >
                Phone Number
              </FormLabel>
              <Box
                sx={{
                  "& .react-tel-input .form-control": {
                    width: "100%",
                    height: "38px",
                    borderRadius: 1,
                    border: `1px solid ${
                      errors.phone ? theme.palette.error.main : "#DBDADE"
                    }`,
                    paddingLeft: "58px",
                    fontSize: "1rem",
                    fontFamily: theme.typography.fontFamily,
                    color: theme.palette.text.primary,
                    "&::placeholder": {
                      color: theme.palette.text.disabled,
                      opacity: 1,
                    },
                    "&:focus": {
                      borderColor: theme.palette.primary.main,
                    },
                  },
                  "& .react-tel-input .flag-dropdown": {
                    borderTopLeftRadius: 6,
                    borderBottomLeftRadius: 6,
                    borderRight: "1px solid #DBDADE",
                  },
                }}
              >
                <PhoneInput
                  country={"us"}
                  onlyCountries={["us"]}
                  disableDropdown={true}
                  disableCountryCode={true}
                  value={phoneNumber || ""}
                  onChange={(phone) =>
                    setPersonalDetails({
                      ...personalDetails,
                      phoneNumber: phone,
                    })
                  }
                  inputClass="form-control"
                  containerStyle={{ width: "100%" }}
                />
              </Box>
              {errors.phone && (
                <FormHelperText
                  sx={{
                    color: "error.main",
                    mt: 1,
                    ml: 1,
                    fontSize: "0.82rem",
                  }}
                >
                  {errors.phone}
                </FormHelperText>
              )}
            </Box>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <FormControl
            fullWidth
            sx={{ display: "flex", justifyContent: "center" }}
          >
           {/* <CustomTextField
          fullWidth
          required
          label="How did you hear about Cleri?"
          placeholder="Please select"
          value={referredBy || ""}
          onChange={(e) =>
            setPersonalDetails({
              ...personalDetails,
              referredBy: e.target.value,
            })
          }
          error={!!errors.referralSource}
          helperText={errors.referralSource}
          sx={{
            maxWidth: "500px",
            minHeight: "56px",
            "& label": {
              fontSize: "0.8rem",
              fontWeight: 420,
              color: theme.palette.text.primary,
            },
            "& .MuiSelect-select": {
              fontSize: "1rem",
            },
          }}
          SelectProps={{
            displayEmpty: true,
            renderValue: (selected) =>
              selected || (
                <span style={{ color: theme.palette.text.disabled }}>
                  Please select
                </span>
              ),
            MenuProps: {
              PaperProps: {
                style: {
                  maxHeight: 140,
                  overflowY: "auto",
                  marginTop: 6,
                },
              },
            },
          }}
        >
          {[...referralOptions] // Create a copy of the array
            .sort((a, b) => a.localeCompare(b)) // Alphabetize
            .map((option) => (
              <MenuItem
                key={option}
                value={option}
                sx={{
                  minHeight: "30px",
                  paddingY: "4px",
                  fontSize: "0.9rem",
                }}
              >
                {option}
              </MenuItem>
            ))}
        </CustomTextField> */}
        <CustomTextField
                fullWidth
                label="How did you hear about Cleri?"
                placeholder=""
                value={referredBy}
                error={!!errors.referralSource}
                helperText={errors.referralSource}
                onChange={(e) => {                  
                  setPersonalDetails({
                    ...personalDetails,
                    referredBy: e.target.value,
                  });
                }}
                required
                sx={{ maxWidth: "500px" }}
              />
          </FormControl>
        </Grid>       
        <Grid
          item
          xs={12}
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            pr: { xs: 0, sm: 0 },
          }}
        >
          <Button
            variant="contained"
            onClick={onNextClick}
            color="main"
            disabled={loading}
            sx={{
              mt: { xs: 2, sm: 0 },
            }}
          >
            Next
          </Button>
        </Grid>
      </Grid>
    </Box>

    <Backdrop
      sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
      open={loading}
    >
      <CircularProgress color="inherit" />
    </Backdrop>
  </Box>
);
};

export default StepAccountDetails;
